import React, { Component } from 'react';
import { Button, Modal, Form, Dropdown } from 'semantic-ui-react';
import { Formik } from 'formik';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as _ from 'lodash';
import toastr from 'toastr';
import { STATUS_LIST } from '../../../HelperMethods/enums';
import BulkEditTaskList from './BulkEditTaskList';
import { getOriginalTasks, mergeAttributesForBulkEditTasks } from './helper';
import apiService from '../../../service/apiService';
import { determineIfUserHasFieldsDisabled } from '../../../HelperMethods/UserUtilities';
import { VIEWS } from '../../../HelperMethods/constants';
import { ACCESS_OPTIONS, ENTITY_ACCESS_TYPE, RESOURCES } from '../../../HelperMethods/role-resource-constants';
import logUtils from '../../../HelperMethods/LogUtils';
import CustomDropdown from '../../common/CustomDropdown';

class BulkEditTasks extends Component {
  assigneeOptions = memoize((assignees) => {
    const assigneesOption = assignees.map((assignee) => {
      return {
        key: assignee.id,
        value: assignee.id,
        text: `${assignee.firstName} ${assignee.lastName}`,
      };
    });
    return assigneesOption;
  });

  taskTypeOptions = memoize((selectedTasks) => {
    const ttOptions = selectedTasks.map((task) => {
      return {
        key: _.get(task, 'taskType.id', ''),
        value: _.get(task, 'taskType.id', ''),
        text: _.get(task, 'taskType.name', ''),
      };
    });
    return _.uniqBy(ttOptions, 'key');
  });

  constructor(props) {
    super(props);
    this.state = {
      assignees: [],
      isAssigneeLoading: false,
      tasksToBeConsidered: props.selectedTasks,
      taskTypeOptions: this.taskTypeOptions(props.selectedTasks),
    };
    this.statusOptions = Object.keys(STATUS_LIST).map((key) => {
      return {
        key,
        value: STATUS_LIST[key].key,
        text: STATUS_LIST[key].text,
      };
    });
  }

  componentDidMount() {
    this.onFetchingAssignees();
  }

  validate = ({ assigneeIds, status }) => {
    let errors = {};
    if ((!assigneeIds || assigneeIds.length === 0) && !status) {
      const message = 'nothing is selected';
      errors = { message };
    }
    return errors;
  };

  onSaveBulkEdit = ({ assigneeIds, status }) => {
    const {
      handleSaveBulkEdit,
      closeModal,
      tasks,
      loggedInUser: { userName },
    } = this.props;
    const { assignees, tasksToBeConsidered } = this.state;
    closeModal();
    const originalSelectedTasks = getOriginalTasks(tasks, tasksToBeConsidered);
    const updatedTasks = mergeAttributesForBulkEditTasks(
      originalSelectedTasks,
      { assigneeIds, status },
      assignees,
      userName
    );
    handleSaveBulkEdit(updatedTasks);
  };

  onFetchingAssignees = async (reqtasks) => {
    try {
      this.setState({ isAssigneeLoading: true });
      const { currView } = this.props;
      const { tasksToBeConsidered } = this.state;
      if (_.size(reqtasks || tasksToBeConsidered) === 0) {
        toastr.info(
          currView === VIEWS.mapView
            ? 'Selected map area has no tasks. Please select an area on the map with tasks'
            : 'No tasks selected'
        );
      } else {
        const assignees = await apiService.getAssigneeList((reqtasks || tasksToBeConsidered).map((task) => task.id));
        this.setState({
          assignees,
        });
      }
    } catch (e) {
      logUtils.logError(e, 'Error fetching assignee list');
      this.setState({ assignees: [] });
    } finally {
      this.setState({ isAssigneeLoading: false });
    }
  };

  handleTaskFilter = (formik, name, value) => {
    const { selectedTasks } = this.props;
    formik.setFieldValue(name, value, false);
    const filteredtasks = selectedTasks.filter((task) => !value || !value.length || value.includes(task.taskTypeId));
    this.onFetchingAssignees(filteredtasks);
    this.setState({
      tasksToBeConsidered: filteredtasks,
    });
  };

  render() {
    const { open, closeModal, loggedInUser, currView } = this.props;
    const { assignees, isAssigneeLoading, tasksToBeConsidered, taskTypeOptions } = this.state;
    const assigneeOptions = this.assigneeOptions(assignees);
    const assigneeDropdownOptions = { isLoading: isAssigneeLoading, items: assigneeOptions };
    return (
      <div>
        <Modal open={open} size="fullscreen" onClose={closeModal} style={{ marginLeft: '2%' }}>
          <Modal.Header>Bulk Edit</Modal.Header>
          <Modal.Content image scrolling style={{ minHeight: '75vh' }}>
            <Modal.Description>
              <Formik
                initialValues={{ assigneeIds: [], status: '', taskType: [] }}
                onSubmit={this.onSaveBulkEdit}
                validate={this.validate}
              >
                {(formik) => (
                  <Form>
                    {currView === VIEWS.mapView && (
                      <Form.Field>
                        <label style={{ display: 'block', padding: '1% 0 1% 0' }}>Task Type</label>
                        <CustomDropdown
                          clearable
                          placeholder="Select Task Type"
                          fluid
                          search
                          selection
                          multiple
                          options={taskTypeOptions}
                          value={formik.values.taskType}
                          onChange={(e, { name, value }) => this.handleTaskFilter(formik, name, value)}
                          name="taskType"
                          style={{ width: '50%' }}
                        />
                      </Form.Field>
                    )}
                    <Form.Field>
                      <label style={{ display: 'block', padding: '1% 0 1% 0' }}>Assignee</label>
                      <CustomDropdown
                        clearable
                        multiple
                        placeholder="Select Assignee"
                        fluid
                        search
                        selection
                        options={assigneeDropdownOptions}
                        value={formik.values.assigneeIds}
                        onChange={(e, { name, value }) => formik.setFieldValue(name, value, false)}
                        name="assigneeIds"
                        style={{ width: '50%' }}
                      />
                    </Form.Field>
                    <Form.Field>
                      <label style={{ display: 'block', padding: '1% 0 1% 0' }}>Status</label>
                      <Dropdown
                        clearable
                        placeholder="Select Status"
                        fluid
                        search
                        selection
                        options={this.statusOptions}
                        value={formik.values.status}
                        onChange={(e, { name, value }) => formik.setFieldValue(name, value, false)}
                        name="status"
                        style={{ width: '50%' }}
                        disabled={determineIfUserHasFieldsDisabled({
                          resource: RESOURCES.ENTITY.TASK,
                          entityAccessType: ENTITY_ACCESS_TYPE.UPDATE,
                          user: loggedInUser,
                          field: ACCESS_OPTIONS.DISABLED_FIELD.options.STATUS,
                        })}
                      />
                    </Form.Field>
                    {formik.errors.message && <div className="errorText">*{formik.errors.message}</div>}
                    <div style={{ paddingTop: '1%' }}>
                      <Button
                        primary
                        type="button"
                        style={{ width: '13%', height: '48px', margin: '0', marginRight: '1%' }}
                        onClick={formik.handleSubmit}
                        disabled={!tasksToBeConsidered || !tasksToBeConsidered.length}
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        style={{ width: '13%', height: '48px', margin: '0', marginRight: '1%' }}
                        onClick={closeModal}
                      >
                        Cancel
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
              <hr />
              {tasksToBeConsidered.length} tasks selected
              <BulkEditTaskList selectedTasks={tasksToBeConsidered} />
            </Modal.Description>
          </Modal.Content>
        </Modal>
      </div>
    );
  }
}
BulkEditTasks.propTypes = {
  open: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  handleSaveBulkEdit: PropTypes.func.isRequired,
  selectedTasks: PropTypes.arrayOf(PropTypes.object).isRequired,
  tasks: PropTypes.arrayOf(PropTypes.object).isRequired,
  loggedInUser: PropTypes.object.isRequired,
  currView: PropTypes.string.isRequired,
};

function mapStateToProps(state) {
  const {
    appReducer: { loggedInUser },
  } = state;
  return { loggedInUser };
}

export default connect(mapStateToProps, null)(BulkEditTasks);
